// src/lib/opportunity/proposalContactTypes.ts

import { FORMAT_CALC_PRODUCT_TYPE_WILDCARD } from "./formatCalcLineTypes.js";
import { formatProposalContactPhone } from "./proposalContactDisplay.js";
import type { OpportunityFormatKey } from "../../types/opportunity.js";

export const PROPOSAL_CONTACT_ORG_NAME_MAX_LEN = 200;
export const PROPOSAL_CONTACT_PERSON_NAME_MAX_LEN = 200;
export const PROPOSAL_CONTACT_ADDRESS_MAX_LEN = 500;
export const PROPOSAL_CONTACT_PHONE_MAX_LEN = 64;
export const PROPOSAL_CONTACT_EMAIL_MAX_LEN = 200;

export type ProposalContactInfo = {
  orgName: string;
  personName: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
};

export type OrgProposalContactRow = {
  proposal_contact_org_name: string | null;
  proposal_contact_person_name: string | null;
  proposal_contact_address: string | null;
  proposal_contact_phone: string | null;
  proposal_contact_email: string | null;
};

export type FormatProposalContact = {
  id: string;
  organization_id: string;
  format_key: OpportunityFormatKey;
  product_type: string;
  org_name: string | null;
  person_name: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  allow_rep_contact_override: boolean;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type FormatProposalContactDef = Pick<
  FormatProposalContact,
  | "org_name"
  | "person_name"
  | "address"
  | "phone"
  | "email"
  | "allow_rep_contact_override"
>;

export function orgRowToProposalContact(
  row: OrgProposalContactRow,
  brandNameFallback: string,
  organizationNameFallback = "",
): ProposalContactInfo {
  const orgName =
    row.proposal_contact_org_name?.trim() ||
    brandNameFallback.trim() ||
    organizationNameFallback.trim() ||
    "Organization";
  return {
    orgName,
    personName: row.proposal_contact_person_name?.trim() || null,
    address: row.proposal_contact_address?.trim() || null,
    phone: row.proposal_contact_phone?.trim() || null,
    email: row.proposal_contact_email?.trim() || null,
  };
}

export function mergeProposalContact(
  base: ProposalContactInfo,
  override: Partial<FormatProposalContactDef> | null | undefined,
): ProposalContactInfo {
  if (!override) return base;
  return {
    orgName: override.org_name?.trim() || base.orgName,
    personName: override.person_name?.trim() || base.personName,
    address: override.address?.trim() || base.address,
    phone: override.phone?.trim() || base.phone,
    email: override.email?.trim() || base.email,
  };
}

export function selectFormatProposalContact(args: {
  rows: FormatProposalContact[];
  productType: string;
}): FormatProposalContactDef | null {
  const specific = args.rows.filter((r) => r.product_type === args.productType);
  const wildcard = args.rows.filter((r) => r.product_type === FORMAT_CALC_PRODUCT_TYPE_WILDCARD);
  const row = specific[0] ?? wildcard[0];
  if (!row) return null;
  return {
    org_name: row.org_name,
    person_name: row.person_name,
    address: row.address,
    phone: row.phone,
    email: row.email,
    allow_rep_contact_override: row.allow_rep_contact_override,
  };
}

export function formatProposalContactLines(contact: ProposalContactInfo): string[] {
  const lines: string[] = [contact.orgName];
  if (contact.personName) lines.push(contact.personName);
  if (contact.address) lines.push(contact.address);
  const phoneDisplay = formatProposalContactPhone(contact.phone);
  const phoneEmail = [phoneDisplay, contact.email].filter(Boolean).join(" · ");
  if (phoneEmail) lines.push(phoneEmail);
  return lines;
}
